import { useQuery } from "@apollo/client";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams.svg";
import { ReactNode } from "react";
import {
  GetOrganizationNotificationsQuery,
  GetOrganizationNotificationsQueryVariables,
  OrgSettingsOrganizationFragmentFragment,
} from "types/graphql-schema";

import SlackNotification from "@apps/org-settings/components/slack-notification";
import TeamsNotification from "@apps/org-settings/components/teams-notification";
import useLabel from "@apps/use-label/use-label";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  getAssetUrl,
} from "@helpers/helpers";

import getOrganizationNotifications from "../graphql/get-organization-notifications";

const NotificationSection = ({
  logo,
  logoAlt,
  showLogo,
  children,
}: {
  logo: string;
  logoAlt: string;
  showLogo: boolean;
  children: ReactNode;
}) => (
  <section
    className="bg-white py-4 flex"
    aria-label={`${logoAlt} notifications`}
  >
    {showLogo && (
      <div className="col font-medium mb-4 flex items-start w-28 shrink-0">
        <div className="border rounded-sm flex justify-center items-center h-24 w-28 p-4">
          <img
            src={getAssetUrl(logo)}
            alt={logoAlt}
            className="object-contain h-16"
          />
        </div>
      </div>
    )}
    <div
      className={`flex flex-col divide-y divide-gray-100 ${showLogo ? "pl-5" : ""}`}
    >
      {children}
    </div>
  </section>
);

const Notifications = ({
  organization,
  showLogo = true,
  slackOnly = false,
}: {
  organization: OrgSettingsOrganizationFragmentFragment;
  showLogo?: boolean;
  slackOnly?: boolean;
}) => {
  const label = useLabel();
  const { data } = useQuery<
    GetOrganizationNotificationsQuery,
    GetOrganizationNotificationsQueryVariables
  >(getOrganizationNotifications, {
    variables: { organizationId: organization.id },
  });
  const queriedOrganization = data?.organization;
  const hasSlackConnection =
    queriedOrganization?.hasSlackConnection ?? organization.hasSlackConnection;
  const hasTeamsConnection = Boolean(
    queriedOrganization?.hasTeamsNotificationConnection ??
      organization.hasTeamsNotificationConnection,
  );
  const slackNotifications = queriedOrganization?.slackNotifications
    ? assertEdgesNonNull(queriedOrganization.slackNotifications)
    : [];
  const slackChannels = queriedOrganization?.publicSlackChannels
    ? assertEdgesNonNullWithStringId(queriedOrganization.publicSlackChannels)
    : [];
  const slackChannelOptions = slackChannels.map((channel) => ({
    label: `#${channel.name}`,
    value: channel.id,
    ...channel,
  }));
  const existingSlackChannelIds = slackNotifications.map(
    ({ channel }) => channel,
  );

  const teamsNotifications = queriedOrganization?.teamsNotifications
    ? assertEdgesNonNull(queriedOrganization.teamsNotifications)
    : [];
  const teamsChannelOptions = (
    queriedOrganization?.availableTeamsNotificationChannels || []
  )
    .filter(
      (channel): channel is NonNullable<typeof channel> => channel !== null,
    )
    .map((channel) => ({
      label: `${channel.teamName} / ${channel.channelName}`,
      value: `${channel.teamId}/${channel.channelId}`,
      ...channel,
    }));
  const existingTeamsChannelIds = teamsNotifications.map(
    ({ teamId, channelId }) => `${teamId}/${channelId}`,
  );
  const recognitionLabel = label("recognition", { pluralize: true });
  const goalLabel = label("goal", { pluralize: false });

  return (
    <div>
      {showLogo && (
        <h2 className="text-2xl pb-2 font-medium text-gray-800">
          Notifications
        </h2>
      )}

      {hasSlackConnection && (
        <NotificationSection
          logo={slackLogo}
          logoAlt="Slack"
          showLogo={showLogo}
        >
          <div>
            Send notifications of {recognitionLabel} or {goalLabel} check-ins to
            one or more Slack channels
          </div>
          {slackNotifications.map((notification) => (
            <SlackNotification
              key={notification.id}
              channels={slackChannelOptions}
              slackNotification={notification}
              organization={organization}
              existingChannelIds={existingSlackChannelIds}
            />
          ))}
          <SlackNotification
            channels={slackChannelOptions}
            slackNotification={null}
            organization={organization}
            existingChannelIds={existingSlackChannelIds}
          />
        </NotificationSection>
      )}

      {!slackOnly && hasTeamsConnection && (
        <NotificationSection
          logo={teamsLogo}
          logoAlt="Teams"
          showLogo={showLogo}
        >
          <div>
            Send notifications of {recognitionLabel} or {goalLabel} check-ins to
            one or more Teams channels
          </div>
          {teamsNotifications.map((notification) => (
            <TeamsNotification
              key={notification.id}
              channels={teamsChannelOptions}
              teamsNotification={notification}
              organization={organization}
              existingChannelIds={existingTeamsChannelIds}
            />
          ))}
          {teamsChannelOptions.length > 0 ? (
            <TeamsNotification
              channels={teamsChannelOptions}
              teamsNotification={null}
              organization={organization}
              existingChannelIds={existingTeamsChannelIds}
            />
          ) : (
            <p className="py-4 text-sm text-gray-600">
              Add the Topicflow app to the Microsoft Team where you want to send
              notifications. Its standard channels will appear here after Teams
              confirms the installation.
            </p>
          )}
        </NotificationSection>
      )}

      {!slackOnly && !hasSlackConnection && !hasTeamsConnection && (
        <div className="rounded-sm border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Connect Slack or Microsoft Teams to send {recognitionLabel} and{" "}
          {goalLabel} check-in notifications to chat channels.
        </div>
      )}
    </div>
  );
};

export default Notifications;
